"use client";

import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X } from "lucide-react";
import { useTranslation } from "react-i18next";

interface LoginModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const WHATSAPP_NUMBER = "6281370000299";

export default function LoginModal({ isOpen, onClose }: LoginModalProps) {
  const { t } = useTranslation();
  const [schoolSlug, setSchoolSlug] = useState("");

  const demoWaUrl = `https://api.whatsapp.com/send/?phone=${WHATSAPP_NUMBER}&text=${encodeURIComponent(
    t("login_modal.wa_demo_message")
  )}&type=phone_number&app_absent=0`;
  const supportWaUrl = `https://api.whatsapp.com/send/?phone=${WHATSAPP_NUMBER}&text=${encodeURIComponent(
    t("login_modal.wa_support_message")
  )}&type=phone_number&app_absent=0`;

  const cleanSlug = schoolSlug.trim().toLowerCase().replace(/[^a-z0-9-]/g, "");

  const goToWebsite = () => {
    if (!cleanSlug) return;
    window.open(`https://${cleanSlug}.edunav.net`, "_blank", "noopener,noreferrer");
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50"
          />

          <div className="fixed inset-0 z-50 flex items-center justify-center p-3 md:p-4">
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              transition={{ type: "spring", duration: 0.5 }}
              className="relative w-full max-w-[calc(100vw-1.5rem)] sm:max-w-2xl md:max-w-3xl overflow-hidden rounded-[1.5rem] sm:rounded-[2rem] bg-gradient-to-br from-[#0ea5e9] to-[#075985] p-5 sm:p-8 md:p-12 text-white shadow-2xl"
            >
              <button
                onClick={onClose}
                aria-label="Close"
                className="absolute right-4 top-4 sm:right-5 sm:top-5 flex h-8 w-8 sm:h-9 sm:w-9 items-center justify-center rounded-full bg-white/10 text-white transition hover:bg-white/20"
              >
                <X size={16} className="sm:hidden" />
                <X size={18} className="hidden sm:block" />
              </button>

              <h2 className="text-center text-xl sm:text-3xl md:text-4xl font-extrabold leading-tight pr-8 sm:pr-0">
                {t("login_modal.title_line1")}
                <br />
                {t("login_modal.title_line2")}
              </h2>

              <div className="mt-6 sm:mt-8 mx-auto flex max-w-md items-stretch overflow-hidden rounded-xl sm:rounded-2xl bg-white text-slate-900 shadow-lg">
                <input
                  type="text"
                  value={schoolSlug}
                  onChange={(e) => setSchoolSlug(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && goToWebsite()}
                  placeholder={t("login_modal.input_placeholder")}
                  className="min-w-0 flex-1 px-3 py-3 sm:px-5 sm:py-4 text-xs sm:text-sm font-semibold placeholder:text-slate-400 focus:outline-none"
                />
                <span className="flex items-center gap-2 whitespace-nowrap border-l border-slate-200 px-3 sm:px-4 text-xs sm:text-sm font-medium text-slate-400">
                  .edunav.net
                </span>
              </div>
              <p
                className="mt-3 text-center text-[11px] sm:text-xs md:text-sm text-white/90"
                dangerouslySetInnerHTML={{ __html: t("login_modal.hint") }}
              />

              <div className="mt-5 sm:mt-6 flex justify-center">
                <button
                  onClick={goToWebsite}
                  disabled={!cleanSlug}
                  className="rounded-full border border-white/60 bg-[#0369a1] px-6 sm:px-8 py-2.5 sm:py-3 text-xs sm:text-sm font-bold text-white transition hover:bg-[#075985] disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {t("login_modal.btn_go")}
                </button>
              </div>

              <hr className="mt-6 sm:mt-8 border-white/30" />

              <div className="mt-4 sm:mt-5 flex flex-col gap-4 text-[11px] sm:text-xs md:text-sm text-white/90 sm:flex-row sm:justify-between">
                <p>
                  <span dangerouslySetInnerHTML={{ __html: t("login_modal.demo_text") }} />{" "}
                  <a href={demoWaUrl} target="_blank" rel="noopener noreferrer" className="font-bold hover:underline">
                    {t("login_modal.klik_disini")}
                  </a>
                </p>
                <p className="sm:text-right">
                  <span dangerouslySetInnerHTML={{ __html: t("login_modal.support_text") }} />{" "}
                  <a href={supportWaUrl} target="_blank" rel="noopener noreferrer" className="font-bold hover:underline">
                    {t("login_modal.klik_disini")}
                  </a>
                </p>
              </div>
            </motion.div>
          </div>
        </>
      )}
    </AnimatePresence>
  );
}
